<template>
  <div
    v-if="items.length"
    class="dealership-picker"
    :class="`dealership-picker--${layout}`"
    role="radiogroup"
    :aria-label="label"
  >
    <button
      v-for="item in items"
      :key="item.id"
      type="button"
      role="radio"
      class="dealership-picker__item"
      :class="{ 'dealership-picker__item--selected': item.id === markedId }"
      :aria-checked="item.id === markedId"
      @click="choose(item.id)"
    >
      <AvatarChip
        class="dealership-picker__avatar"
        :initial="initialOf(item.name)"
        :seed="item.id"
        :size="avatarSize"
      />
      <span class="dealership-picker__text">
        <span class="dealership-picker__name">{{ item.name }}</span>
        <span class="dealership-picker__meta">{{ item.meta }}</span>
      </span>
      <span
        v-if="layout === 'list'"
        class="dealership-picker__check"
        :class="{ 'dealership-picker__check--on': item.id === markedId }"
        aria-hidden="true"
      >
        <i v-if="item.id === markedId" class="pi pi-check" />
      </span>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { AvatarChip } from '@hds/auto-auction-com-components-shared';

// Who the dealer bids as: one radiogroup of dealerships, drawn either as the
// BidSheet's horizontal strip of chips or as the Dashboard / VideoView sheet's
// vertical list with a check.
//
// Three screens each wrote this radiogroup, and two of them skipped AvatarChip
 // so the avatar colours drifted from BidSheet's. The roster shape also drifted:
 // BidSheet keyed on `dealership.id`, the portal sheets on `identity.badge`.
//
// Presentation only. The roster arrives as `{ id, name, meta }` — meta is
 // pre-formatted at the call site ("Rep · #1042", "1042 · Primary") so the
 // picker never formats a role or invents an account number.
//
// Absent rather than empty when the roster is: a labelled radiogroup with no
 // radios announces a choice that is not being offered.
defineOptions({ name: 'DealershipPicker' });

const props = defineProps({
  // `[{ id, name, meta }]`. `meta` is the second line, already written.
  items: {
    type: Array,
    required: true,
    validator: (items) => Array.isArray(items) && items.every((item) => (
      typeof item?.id === 'string' && item.id.length > 0
      && typeof item?.name === 'string' && item.name.length > 0
      && typeof item?.meta === 'string'
    )),
  },
  // What a reader hears for the group — "Bidding as", "Bid on behalf of".
  label: { type: String, required: true },
  // 'strip' — BidSheet's horizontal chip row.
  // 'list'  — the sheet's vertical rows with a check mark.
  layout: {
    type: String,
    default: 'list',
    validator: (value) => ['strip', 'list'].includes(value),
  },
});

/** The chosen dealership's `id`. */
const selectedId = defineModel({ type: String, default: null });

const avatarSize = computed(() => (props.layout === 'strip' ? 26 : 38));

// A roster missing its current id falls back to marking the first entry, so the
// picker and the consumer agree about who is bidding rather than disagreeing in
 // a way nothing catches.
const markedId = computed(() => {
  const known = props.items.some((entry) => entry.id === selectedId.value);
  return known ? selectedId.value : props.items[0]?.id ?? null;
});

const initialOf = (name) => String(name).trim().charAt(0).toUpperCase();

function choose(id) {
  if (id === selectedId.value) return;
  selectedId.value = id;
}
</script>

<style scoped>
.dealership-picker--strip {
  display: flex;
  gap: var(--p-dealership-picker-strip-gap);
  overflow-x: auto;
  scrollbar-width: none;
  margin-bottom: var(--p-dealership-picker-strip-margin-bottom);
}

.dealership-picker--strip::-webkit-scrollbar { display: none; }

.dealership-picker--list {
  display: flex;
  flex-direction: column;
  gap: var(--p-dealership-picker-list-gap);
}

.dealership-picker__item {
  display: flex;
  align-items: center;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  color: var(--p-dealership-picker-item-color);
}

.dealership-picker--strip .dealership-picker__item {
  flex: none;
  gap: var(--p-dealership-picker-strip-item-gap);
  max-width: var(--p-dealership-picker-strip-item-max-width);
  padding: var(--p-dealership-picker-strip-item-padding);
  border-radius: var(--p-dealership-picker-strip-item-border-radius);
  background: var(--p-dealership-picker-strip-item-background);
  border: 1px solid var(--p-dealership-picker-strip-item-border-color);
}

.dealership-picker--strip .dealership-picker__item--selected {
  background: var(--p-dealership-picker-strip-item-selected-background);
  border-color: var(--p-dealership-picker-strip-item-selected-border-color);
  color: var(--p-dealership-picker-strip-item-selected-color);
}

/* The chosen chip is inverted, so its second line takes the inverted ink too. */
.dealership-picker--strip .dealership-picker__item--selected .dealership-picker__meta {
  color: var(--p-dealership-picker-strip-item-selected-color);
}

.dealership-picker--list .dealership-picker__item {
  gap: var(--p-dealership-picker-list-item-gap);
  width: 100%;
  padding: var(--p-dealership-picker-list-item-padding);
  border-radius: var(--p-dealership-picker-list-item-border-radius);
  background: var(--p-dealership-picker-list-item-background);
  border: 1px solid var(--p-dealership-picker-list-item-border-color);
}

.dealership-picker--list .dealership-picker__item--selected {
  background: var(--p-dealership-picker-list-item-selected-background);
  border-color: var(--p-dealership-picker-list-item-selected-border-color);
}

.dealership-picker__text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.dealership-picker__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--p-dealership-picker-name-font-size);
  font-weight: var(--p-dealership-picker-name-font-weight);
}

.dealership-picker--list .dealership-picker__name {
  font-size: var(--p-dealership-picker-list-name-font-size);
  letter-spacing: -0.01em;
}

.dealership-picker__meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--p-dealership-picker-meta-font-size);
  color: var(--p-dealership-picker-meta-color);
  font-family: var(--p-dealership-picker-meta-font-family);
}

.dealership-picker--list .dealership-picker__meta {
  margin-top: 1px;
  color: var(--p-dealership-picker-list-meta-color);
}

.dealership-picker__check {
  width: 24px;
  height: 24px;
  border-radius: 12px;
  flex-shrink: 0;
  border: 1.5px solid var(--p-dealership-picker-check-border-color);
  display: grid;
  place-items: center;
}

.dealership-picker__check--on {
  background: var(--p-dealership-picker-check-on-background);
  border-color: var(--p-dealership-picker-check-on-border-color);
  color: var(--p-dealership-picker-check-on-color);
}

.dealership-picker__item:focus-visible {
  outline: 2px solid var(--p-dealership-picker-focus-outline-color);
  outline-offset: 2px;
}
</style>
